import { DeepPartial, mergeDeep } from "common/utils/objects";
import { DimensionedNumberInput } from "common/components/DimensionedNumberInput";
import { formatMeasurement } from "portal/components/measurement/formatters";
import { IconButton, Typography } from "@mui/material";
import { titleCase } from "common/utils/strings";
import { useCanEditFarmGeometry } from "portal/views/farms/permissions";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { Zone as ZoneType } from "protos/farm/farm";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import React, { FC, useState } from "react";

interface ZoneBufferEditorProps {
  zone: ZoneType;
  updateZone: (newZone: DeepPartial<ZoneType>) => void;
}
export const ZoneBufferEditor: FC<ZoneBufferEditorProps> = ({
  zone,
  updateZone,
}) => {
  const [buffer, setBuffer] = useState<number | undefined>(
    zone.areas[0]?.bufferMeters
  );
  const { t, i18n } = useTranslation();
  const [editingBufferId, setEditingBufferId] = useState<string | undefined>();
  const isEditing = editingBufferId === zone.id?.id;
  const [lastZoneId, setLastZoneId] = useState<string | undefined>(zone.id?.id);

  if (lastZoneId !== zone.id?.id) {
    setBuffer(zone.areas[0]?.bufferMeters);
    setLastZoneId(zone.id?.id);
  }
  const { measurementSystem } = useSelf();
  const canEditGeometry = useCanEditFarmGeometry();

  return isEditing && canEditGeometry ? (
    <div className="flex gap-1 px-2">
      <DimensionedNumberInput
        label={titleCase(t("models.farms.buffer_one"))}
        storedValue={buffer ?? 0}
        storedUnit="m"
        displayUnits={{ imperial: "ft", metric: "m" }}
        localSystem={measurementSystem}
        onChange={(storedValue: number | undefined) => {
          setBuffer(storedValue);
        }}
        minStoredValue={0}
      />
      <div className="flex ml-auto">
        <IconButton
          color="info"
          disabled={buffer === zone.areas[0]?.bufferMeters}
          onClick={() => {
            const area = zone.areas[0];
            if (area && buffer !== undefined) {
              const newArea = { ...area, bufferMeters: buffer };
              const updatedZone = mergeDeep(zone, {
                areas: [newArea],
                version: {
                  changed: true,
                },
              });
              updateZone(updatedZone);
              setEditingBufferId(undefined);
            }
          }}
        >
          <CheckIcon />
        </IconButton>
        <IconButton
          color="info"
          onClick={() => {
            setEditingBufferId(undefined);
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>
    </div>
  ) : (
    <div className="flex gap-1 px-2">
      <Typography className="flex my-auto">
        {titleCase(t("models.farms.buffer_one"))}
      </Typography>
      <div className="flex ml-auto">
        <Typography className="flex my-auto">
          {formatMeasurement(
            t,
            i18n,
            measurementSystem,
            buffer,
            "m"
          ).toString()}
        </Typography>
        {canEditGeometry && (
          <IconButton
            color="info"
            onClick={() => {
              setEditingBufferId(zone.id?.id);
            }}
          >
            <EditIcon />
          </IconButton>
        )}
      </div>
    </div>
  );
};
